"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import {
  ArrowLeft,
  ClipboardCheck,
  Loader2,
  LogOut,
  MessageSquare,
  User,
} from "lucide-react"

import { createClient } from "@/lib/supabase/client"

import { useCurrentCourse } from "@/contexts/CurrentCourseContext"

import { DzpLogo } from "./DzpLogo"
import { NotificationBell } from "./NotificationBell"

type UserProfile = {
  first_name: string | null
  avatar_url: string | null
  role: string
}

export const NON_COURSE_LEARN_SEGMENTS = [
  "calendar",
  "simulados",
  "speaking",
  "corrections",
  "comments",
  "practice",
]

const GLOBAL_NAV = [
  { label: "Início", href: "/", exact: true },
  { label: "Calendário", href: "/calendar" },
  { label: "Cursos", href: "/courses", exact: true },
]

export function Header() {
  const pathname = usePathname()
  const { courses } = useCurrentCourse()
  const supabase = useMemo(() => createClient(), [])
  const menuRef = useRef<HTMLDivElement>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node))
        setMenuOpen(false)
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [])

  useEffect(() => {
    async function loadProfile() {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return
      const { data } = await supabase
        .from("profiles")
        .select("first_name, avatar_url, role")
        .eq("id", user.id)
        .single()
      if (data) setProfile(data)
    }
    void loadProfile()
  }, [supabase])

  const isFocusPage =
    /^\/learn\/[^/]+\/lesson\/[^/]+$/.test(pathname) ||
    /^\/learn\/simulados\/[^/]+\/tentativa$/.test(pathname)
  if (
    isFocusPage ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/partners")
  )
    return null

  const pathCourseId =
    pathname.match(/^\/learn\/([^/]+)\/(content|challenge)/)?.[1] ?? null
  const course = courses.find((item) => item.id === pathCourseId)
  const courseNav = pathCourseId
    ? [
        { label: "Aulas", href: `/learn/${pathCourseId}/content` },
        ...(course?.hasChallenge
          ? [{ label: "Desafio", href: `/learn/${pathCourseId}/challenge` }]
          : []),
      ]
    : GLOBAL_NAV
  const isStaff = profile?.role === "admin" || profile?.role === "partner"
  const isStudentArea =
    pathname.startsWith("/learn") ||
    pathname.startsWith("/courses") ||
    pathname.startsWith("/calendar")
  const initials = profile?.first_name?.slice(0, 1).toUpperCase() || "?"

  async function signOut() {
    setSigningOut(true)
    await supabase.auth.signOut()
    window.location.href = "/login"
  }

  return (
    <header className="sticky top-0 z-50 h-14 shrink-0 border-b border-black/[0.06] bg-white/95 backdrop-blur">
      <div className="flex h-full items-center justify-between px-4 sm:px-8">
        <div className="flex items-center gap-7">
          <DzpLogo
            href={
              profile?.role === "admin"
                ? "/admin"
                : profile?.role === "partner"
                  ? "/partners"
                  : "/"
            }
          />
          <nav
            aria-label={
              pathCourseId ? "Navegação do curso" : "Navegação principal"
            }
            className="hidden items-center gap-6 lg:flex"
          >
            {courseNav.map((item) => {
              const active = item.exact
                ? pathname === item.href
                : pathname.startsWith(item.href)
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  className={`relative flex min-h-11 items-center text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-700 ${active ? "font-semibold text-ink-headline" : "text-ink-muted hover:text-ink-headline"}`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute right-0 bottom-0 left-0 h-0.5 rounded-full bg-brand-700" />
                  )}
                </Link>
              )
            })}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {pathCourseId && (
            <Link
              href="/courses"
              className="text-caption hidden h-9 items-center gap-1.5 rounded-lg border border-neutral-200 px-3 text-ink-body sm:flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Voltar aos cursos
            </Link>
          )}
          {isStaff && isStudentArea && (
            <Link
              href={profile?.role === "admin" ? "/admin" : "/partners"}
              className="text-caption hidden h-9 items-center gap-1.5 rounded-lg border border-neutral-200 px-3 text-ink-body sm:flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Voltar ao painel
            </Link>
          )}
          {profile && <NotificationBell />}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label="Abrir menu da conta"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
              className="flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
            >
              {profile?.avatar_url ? (
                <Image
                  src={profile.avatar_url}
                  alt={`Avatar de ${profile.first_name ?? "usuário"}`}
                  referrerPolicy="no-referrer"
                  width={28}
                  height={28}
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-700 text-[10px] font-bold text-white">
                  {initials}
                </span>
              )}
            </button>
            {menuOpen && (
              <div className="absolute top-full right-0 mt-1 w-56 rounded-2xl border border-black/[0.06] bg-white p-2 shadow-xl">
                <Link
                  href="/perfil"
                  onClick={() => setMenuOpen(false)}
                  className="text-body-sm flex min-h-11 items-center gap-3 rounded-xl px-3 text-ink-headline hover:bg-neutral-50"
                >
                  <User className="h-4 w-4" /> Meu perfil
                </Link>
                <Link
                  href="/learn/corrections"
                  onClick={() => setMenuOpen(false)}
                  className="text-body-sm flex min-h-11 items-center gap-3 rounded-xl px-3 text-ink-headline hover:bg-neutral-50"
                >
                  <ClipboardCheck className="h-4 w-4" /> Correções
                </Link>
                <Link
                  href="/learn/comments"
                  onClick={() => setMenuOpen(false)}
                  className="text-body-sm flex min-h-11 items-center gap-3 rounded-xl px-3 text-ink-headline hover:bg-neutral-50"
                >
                  <MessageSquare className="h-4 w-4" /> Comentários
                </Link>
                <div className="my-1 h-px bg-black/[0.06]" />
                <button
                  type="button"
                  onClick={signOut}
                  disabled={signingOut}
                  className="text-body-sm flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-feedback-error-accent hover:bg-feedback-error-bg disabled:opacity-50"
                >
                  {signingOut ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4" />
                  )}
                  {signingOut ? "Saindo…" : "Sair"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
